import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, filter } from 'rxjs';
import { OktaService } from '../okta/okta.service';

/**
 * User permissions interface
 */
export interface UserPermissions {
  canView: boolean;
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
  isReadOnly: boolean;
}

/**
 * Authorization service for role-based access control
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  // ============================================
  // Constants
  // ============================================
  private readonly OKTA_GROUP_EDIT = 'PI STATEMENT MESSAGING EDIT';
  private readonly OKTA_GROUP_VIEW = 'PI STATEMENT MESSAGING VIEW';

  // ============================================
  // Services
  // ============================================
  private readonly oktaService = inject(OktaService);

  // ============================================
  // State
  // ============================================
  private permissionsSubject = new BehaviorSubject<UserPermissions>({
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  });

  public permissions$: Observable<UserPermissions> = this.permissionsSubject.asObservable();

  // ============================================
  // Initialization
  // ============================================
  constructor() {
    this.initializePermissions();
  }

  /**
   * Initialize user permissions from Okta groups
   */
  private initializePermissions(): void {
    this.oktaService
      .getOktaGroupsObservable()
      .pipe(
        //  Filter out undefined/null/empty initial values
        filter((groups) => Array.isArray(groups) && groups.length > 0)
      )
      .subscribe((groups: string[]) => {
        const permissions = this.calculatePermissions(groups);
        this.permissionsSubject.next(permissions);
      });
  }

  /**
   * Calculate user permissions based on Okta groups
   */
  private calculatePermissions(groups: string[]): UserPermissions {
/*    //  Defensive check: ensure groups is an array
    if (!Array.isArray(groups) || groups.length === 0) {
      return {
        canView: false,
        canEdit: false,
        canCreate: false,
        canDelete: false,
        isReadOnly: true,
      };
    }*/

    const hasEditAccess = groups.includes(this.OKTA_GROUP_EDIT);
    const hasViewAccess = groups.includes(this.OKTA_GROUP_VIEW);

    // Edit access includes all permissions
    if (hasEditAccess) {
      return {
        canView: true,
        canEdit: true,
        canCreate: true,
        canDelete: true,
        isReadOnly: false,
      };
    }

    // View-only access
    if (hasViewAccess) {
      return {
        canView: true,
        canEdit: false,
        canCreate: false,
        canDelete: false,
        isReadOnly: true,
      };
    }

    // No access
    return {
      canView: false,
      canEdit: false,
      canCreate: false,
      canDelete: false,
      isReadOnly: true,
    };
  }

  // ============================================
  // Public API - Synchronous
  // ============================================

  /**
   * Get current permissions (synchronous)
   */
  getPermissions(): UserPermissions {
    return this.permissionsSubject.getValue();
  }

  /**
   * Check if user can view
   */
  canView(): boolean {
    return this.permissionsSubject.getValue().canView;
  }

  /**
   * Check if user can edit
   */
  canEdit(): boolean {
    return this.permissionsSubject.getValue().canEdit;
  }

  /**
   * Check if user can create
   */
  canCreate(): boolean {
    return this.permissionsSubject.getValue().canCreate;
  }

  /**
   * Check if user can delete
   */
  canDelete(): boolean {
    return this.permissionsSubject.getValue().canDelete;
  }

  /**
   * Check if user is read-only
   */
  isReadOnly(): boolean {
    return this.permissionsSubject.getValue().isReadOnly;
  }

  // ============================================
  // Public API - Observable
  // ============================================

  /**
   * Observable for view permission
   */
  canView$(): Observable<boolean> {
    return this.permissions$.pipe(map((p) => p.canView));
  }

  /**
   * Observable for edit permission
   */
  canEdit$(): Observable<boolean> {
    return this.permissions$.pipe(map((p) => p.canEdit));
  }

  /**
   * Observable for create permission
   */
  canCreate$(): Observable<boolean> {
    return this.permissions$.pipe(map((p) => p.canCreate));
  }

  /**
   * Observable for delete permission
   */
  canDelete$(): Observable<boolean> {
    return this.permissions$.pipe(map((p) => p.canDelete));
  }

  /**
   * Observable for read-only check
   */
  isReadOnly$(): Observable<boolean> {
    return this.permissions$.pipe(map((p) => p.isReadOnly));
  }
}
